import React, { useEffect, useState } from 'react';
import { AspectRatio, Image } from '@mantine/core';
import {
  DEFAULT_MEDIA_ASPECT_RATIO,
  resolveMediaObjectFit,
  mediaFitVars,
  mediaFitInline,
  usesIntrinsicContainLayout,
} from './mediaFit';
import { useResolvedRatio } from './useMediaAspectRatio';
import classes from './MediaFrame.module.css';
import { useImageFocalPoint } from './useImageFocalPoint';

/**
 * LE composant image du webviewer — unique <Image>, autonome.
 * Même signature que VideoFrame (extras playback en moins) : src, srcSet, alt,
 * mediaFit, mediaAspectRatio, fallbackRatio, radius, className, style,
 * isEditing, asBackground, onError.
 * - asBackground : élément nu qui remplit un parent positionné (fonds Stack)
 * - contenir + ratio auto : hauteur naturelle de l'image (intrinsic)
 * - défaut : cadre AspectRatio verrouillé (préréglage ou ratio détecté)
 * Image cassée : masquée au viewer, visible en édition (reset si src change).
 */
export function ImageFrame({
  src = '',
  /* Résolution intelligente : le navigateur choisit le palier selon l'écran et
     la densité, sans JavaScript — y compris au premier rendu du site publié.
     Pas de `sizes` : sans connaître la largeur réelle du bloc, en poser un
     serait un mensonge, et le défaut (100vw) reste honnête. */
  srcSet = '',
  alt = '',
  mediaFit = 'cover',
  mediaAspectRatio = 'auto',
  /* object-position : cadrage de l'image recadrée (box image du proposal). */
  mediaPosition,
  mediaFocalPoint,
  fallbackRatio = DEFAULT_MEDIA_ASPECT_RATIO,
  radius = 0,
  className = '',
  style,
  isEditing = false,
  asBackground = false,
  onError,
}) {
  const [broken, setBroken] = useState(false);
  const focal = useImageFocalPoint(src, mediaFocalPoint, mediaFit);
  useEffect(() => {
    setBroken(false);
  }, [src]);
  const ratio = useResolvedRatio(src, mediaAspectRatio, fallbackRatio);

  if (!src) return null;
  if (broken && !isEditing) return null;

  const handleError = (e) => {
    setBroken(true);
    if (typeof onError === 'function') onError(e);
  };
  const position = focal.objectPosition ?? mediaPosition;
  const fitInline = position
    ? { ...mediaFitInline(mediaFit), objectPosition: position }
    : mediaFitInline(mediaFit);
  const intrinsic = usesIntrinsicContainLayout(mediaFit, mediaAspectRatio, { asBackground });

  const image = (
    <Image
      ref={focal.ref}
      src={src}
      srcSet={srcSet || undefined}
      alt={alt}
      title={alt}
      fit={resolveMediaObjectFit(mediaFit)}
      radius={radius}
      w={intrinsic ? '100%' : undefined}
      className={[
        classes.media,
        intrinsic ? classes.mediaIntrinsic : '',
        asBackground ? classes.mediaFill : '',
        asBackground ? className : '',
      ].filter(Boolean).join(' ')}
      style={asBackground ? { ...fitInline, ...style } : fitInline}
      onError={handleError}
    />
  );

  if (asBackground) return image;

  if (intrinsic) {
    return (
      <div className={`${classes.frame} ${classes.frameIntrinsic} ${className}`.trim()} style={style}>
        {image}
      </div>
    );
  }
  return (
    <div className={`${classes.frame} ${className}`.trim()} style={{ ...mediaFitVars(mediaFit), ...style }}>
      <AspectRatio ratio={ratio}>{image}</AspectRatio>
    </div>
  );
}
